<!--
 * @Description: 活码管理页面
 * @Author: mdb
 * @Date: 2022-06-17 11:20:27
 * @LastEditTime: 2023-05-05 14:27:07
 * @LastEditors: mdb
-->
<template>
  <dc-layout-main :is-breadcrumb="true">
    <dc-layout-content>
      <cos-table
        serial
        :columns="columns"
        v-model:search="queryForm"
        @fetch="getList"
        ref="tableRef"
        :tableId="props.subdomain === 'fbu' ? 'DcCosFbuActivityCode' : 'DcCosLbuActivityCode'"
      >
        <template #search>
          <a-form :model="queryForm">
            <dc-form-item name="activityName" label="活动名称">
              <a-input v-model:value.trim="queryForm.activityName" :maxlength="100" allowClear placeholder="请输入" />
            </dc-form-item>
            <dc-form-item name="startTime" label="活动时间">
              <CosRangePicker v-model:startTime="queryForm.startTime" v-model:endTime="queryForm.endTime" />
            </dc-form-item>
            <dc-form-item name="partnerId" label="合作伙伴">
              <a-select
                v-model:value="queryForm.partnerId"
                :fieldNames="{ label: 'name', value: 'id' }"
                :options="partnerEnum"
                placeholder="请选择"
                allowClear
              />
            </dc-form-item>
            <dc-form-item name="channelList" label="来源渠道">
              <ParamCodeCascader
                type="search"
                v-model:ids="queryForm.channelList"
                appId="cos_zbu"
                :options="paramCodeData[props.paramCode]"
              />
            </dc-form-item>
            <dc-form-item name="follower" label="活动负责人">
              <UserSelect v-model:value="queryForm.follower" placeholder="请选择" allowClear />
            </dc-form-item>
            <dc-form-item name="enableFlag" label="状态">
              <a-select
                :fieldNames="{ label: 'name' }"
                v-model:value="queryForm.enableFlag"
                allowClear
                placeholder="请选择"
                :options="statusEnum"
              />
            </dc-form-item>
          </a-form>
        </template>

        <template #bodyCell="{ column, record, text }">
          <template v-if="column.dataIndex === 'enableFlag'">
            <dc-status :enums="statusEnum" :value="text ? 1 : 0" />
          </template>
          <template v-if="column.dataIndex === 'channelId'">
            <ChannelText appId="cos_zbu" :options="paramCodeData[props.paramCode]" :value="text" />
          </template>
          <template v-if="column.dataIndex === 'action'">
            <MoreButton>
              <a-button v-permission="permission.edit" type="link" @click="triggerAddOrEdit(record.id)">编辑</a-button>
              <a-button v-permission="permission.delete" type="link" @click="handleDelete(record)">删除</a-button>
              <a-button
                v-permission="permission.enable"
                v-if="!record.enableFlag"
                type="link"
                @click="handleStatus(record)"
                >启用</a-button
              >
              <a-button v-permission="permission.disable" v-else type="link" @click="handleStatus(record)"
                >停用</a-button
              >
              <a-button v-permission="permission.card" type="link" @click="handleCardLink(record)">关联名片</a-button>
              <a-button v-permission="permission.qrcode" type="link" @click="showModal('zh-CN', record)"
                >中文二维码</a-button
              >
              <a-button v-permission="permission.qrcode" type="link" @click="showModal('en-US', record)"
                >英文二维码</a-button
              >
            </MoreButton>
          </template>
        </template>
        <template #button>
          <a-button v-permission="permission.add" type="primary" @click="triggerAddOrEdit()">新增</a-button>
        </template>
      </cos-table>

      <a-modal v-model:visible="visible" :title="qrCodeTitle" @ok="handleOk">
        <template #footer>
          <a-button key="submit" type="primary" @click="handleCopy">复制链接</a-button>
          <a-button key="submit" type="primary" @click="handleOk">下载二维码</a-button>
        </template>
        <vue-qr
          :margin="0"
          :size="400"
          :text="qrCodeUrl"
          :logoSrc="qrCodeLogoEnum[props.subdomain]"
          :logoScale="0.25"
          :logoMargin="6"
          :correctLevel="0"
          ref="qrCodeRef"
        />
      </a-modal>
    </dc-layout-content>
  </dc-layout-main>

  <!-- 关联名片 -->
  <CardLinkModal ref="cardLinkModalRef" @fetch="handleTableFetch()" />
</template>

<script setup lang="ts">
import { reactive, onMounted, computed, ref, unref, PropType } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import vueQr from 'vue-qr/src/packages/vue-qr.vue'
import { message } from '@dc-platform/dc-components'
import ParamCodeCascader from '@/components/CosCascader/components/ParamCodeCascader.vue'
import UserSelect from '@/components/UserSelect/index'
import CosRangePicker from '@/components/CosRangePicker/index'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import { listByParamCodes } from '@/api/common'
import type { ParamCodeItem } from '@/typings/common'
import { downloadFileByBase64 } from '@/utils/index'
import ChannelText from '@/components/CosCascaderText/components/channel.vue'
import CardLinkModal from './components/CardLinkModal.vue'
import {
  activityList,
  activityEnable,
  activityDisabled,
  activityDelete,
  partnerDropList
} from '@/api/marketing/marketingManage'
import Modal from '@/hooks/modalType'
import { ActivityCodeParams, ActivityCodeListItem } from '@/typings/api/marketingManage'
const router = useRouter()
const { clipboardRef, copiedRef } = useCopyToClipboard()

const props = defineProps({
  subdomain: {
    type: String as PropType<'fbu' | 'lbu'>,
    required: true
  },
  paramCode: {
    type: String as PropType<string>,
    required: true
  },
  permission: {
    type: Object as PropType<{
      add: string
      edit: string
      delete: string
      enable: string
      disable: string
      qrcode: string
      card: string
    }>,
    default: () => ({}),
    required: true
  }
})

const statusEnum = [
  { name: '启用', value: 1, color: '#3FAC46' },
  { name: '停用', value: 0, color: '#DF443B' }
]
const tableRef = ref()
const queryForm = reactive<ActivityCodeParams>({})

const columns = computed(() => [
  {
    title: '活动名称',
    dataIndex: 'activityName',
    cosType: 'link',
    path: `/${props.subdomain}/activityCode/view`,
    query: ['id'],
    fixed: 'left'
  },
  { title: '来源渠道', dataIndex: 'channelId' },
  { title: '活动时间', dataIndex: ['startTime', 'endTime'], cosType: 'timeRange' },
  { title: '活码获客量', dataIndex: 'salesCount', cosType: 'link', path: `/${props.subdomain}/leadManage`, query: ['channelExplain=$activityName'] },
  { title: '活动负责人', dataIndex: 'followerName' },
  { title: '备注', dataIndex: 'remark' },
  { title: '状态', dataIndex: 'enableFlag' },
  { title: '创建人', dataIndex: 'createByName' },
  { title: '创建时间', dataIndex: 'createTime', width: 190 },
  { title: '操作', dataIndex: 'action', width: 170, fixed: 'right' }
])
const getList = (queryForm: ActivityCodeParams, callBack: (res: any) => void) => {
  activityList(queryForm).then((res) => {
    callBack(res)
  })
}
const handleTableFetch = () => {
  tableRef.value?.fetch()
}

const paramCodeData = ref<{
  [key: string]: ParamCodeItem[]
}>({
  [props.paramCode]: [] // 来源渠道
})
// 获取业务参数
const getByParamCodes = () => {
  listByParamCodes({
    appId: 'cos_zbu',
    paramCodes: [props.paramCode]
  }).then((res) => {
    paramCodeData.value = res.data
  })
}
const partnerEnum = ref()
const getPartner = () => {
  partnerDropList().then((res) => {
    partnerEnum.value = res.data
  })
}
onMounted(() => {
  getByParamCodes()
  getPartner()
})

// 关联名片弹窗
const cardLinkModalRef = ref()
const handleCardLink = (rows?: ActivityCodeListItem) => {
  cardLinkModalRef.value?.init(rows)
}

// 新增编辑函数
const triggerAddOrEdit = (id?: number) => {
  if (id) {
    router.push({ path: `/${props.subdomain}/activityCode/edit`, query: { id } })
  } else {
    router.push(`/${props.subdomain}/activityCode/add`)
  }
}
const handleStatus = (row: ActivityCodeListItem) => {
  const params = { activityQrcodeId: row.id }
  if (row.enableFlag) {
    activityDisabled(params).then(() => {
      message.success('活码已停用')
      tableRef.value?.fetch()
    })
  } else {
    const isExpired = dayjs(row.endTime).valueOf() < dayjs().valueOf()
    if (!row.enableFlag && isExpired) return message.warning('活动已过期，无法启用')
    activityEnable(params).then(() => {
      message.success('活码已启用')
      tableRef.value?.fetch()
    })
  }
}
const handleDelete = (row: ActivityCodeListItem) => {
  Modal.delete({
    name: row.activityName,
    onOk() {
      activityDelete({ activityQrcodeId: row.id }).then(() => {
        message.success('删除活码成功')
        tableRef.value?.delFetch()
      })
    }
  })
}

const qrCodeRef = ref()
const qrCodeTitle = ref('')
const visible = ref<boolean>(false)
const currentRow = ref<ActivityCodeListItem>()
const qrCodeType = ref<'zh-CN' | 'en-US'>()
const qrCodeUrl = ref('')
const qrCodeLogoEnum = {
  fbu: require('@/assets/logo_gucang.png'),
  lbu: require('@/assets/logo_yuntu.png')
}
const handleCopy = () => {
  clipboardRef.value = qrCodeUrl.value
  if (unref(copiedRef)) {
    message.success('活动链接复制成功')
  } else {
    message.error('该浏览器不支持自动复制!')
  }
}
const showModal = (lang: 'zh-CN' | 'en-US', row: any) => {
  const langEnum = { 'zh-CN': '中文二维码', 'en-US': '英文二维码' }
  qrCodeType.value = lang
  qrCodeTitle.value = langEnum[lang]
  currentRow.value = row
  qrCodeUrl.value = `${window.WEB_URL}/mobile.html#/activity?qrcodeId=${row.id}&templateId=${row.activityTemplateId}&subdomain=${props.subdomain}&lang=${lang}`
  visible.value = true
}
const handleOk = () => {
  downloadFileByBase64(qrCodeRef.value.imgUrl, `二维码-${currentRow.value?.activityName}-${qrCodeType.value}`)
  visible.value = false
}
</script>
<script lang="ts">
export default { name: 'DcCosZbuActivityCode' }
</script>

<style scoped></style>
